import { useCallback, useState } from "react";
import { notification } from "antd";
import {
  isInIframe,
  NOTIFICATION_MESSAGES,
  requestUnifiedDownload,
} from "@/shared/utils";
import {
  buildFileStoreUrl,
  fetchFilestoreFileBlob,
  fetchS3FileBlob,
  fetchS3FileUrl,
  normalizeFilestorePath,
  resolveDocumentSource,
  type DocumentSource,
  type ResolveDocumentSourceParams,
} from "../../document";

export type DownloadDocumentParams = ResolveDocumentSourceParams & {
  fileName: string;
  localFile?: File | null;
};

function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

async function downloadFromUrl(fileUrl: string, fileName: string) {
  const response = await fetch(fileUrl);

  if (!response.ok) {
    throw new Error("download failed");
  }

  const blob = await response.blob();
  downloadBlob(blob, fileName);
}

async function resolveRemoteFileUrl(source: DocumentSource): Promise<string> {
  switch (source.kind) {
    case "filestore":
      return buildFileStoreUrl(normalizeFilestorePath(source.path));
    case "s3":
      return fetchS3FileUrl(source.fileId);
    case "presigned-url":
      return source.url;
    default:
      throw new Error("unsupported source");
  }
}

async function downloadRemoteSource(
  source: DocumentSource,
  fileName: string,
) {
  if (isInIframe()) {
    const fileUrl = await resolveRemoteFileUrl(source);
    requestUnifiedDownload({ fileUrl, fileName });
    return;
  }

  switch (source.kind) {
    case "filestore": {
      const blob = await fetchFilestoreFileBlob(
        normalizeFilestorePath(source.path),
      );
      downloadBlob(blob, fileName);
      break;
    }
    case "s3": {
      const blob = await fetchS3FileBlob(source.fileId);
      downloadBlob(blob, fileName);
      break;
    }
    case "presigned-url": {
      await downloadFromUrl(source.url, fileName);
      break;
    }
    default:
      throw new Error("unsupported source");
  }
}

export const useDocumentDownloadAction = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadDocument = useCallback(
    async ({
      documentUrl,
      fileId,
      blobUrl,
      fileName,
      localFile,
    }: DownloadDocumentParams) => {
      const resolvedFileName = fileName || "document.pdf";
      const hasLocalFile = Boolean(localFile && localFile.size > 0);
      const source = resolveDocumentSource({ documentUrl, fileId, blobUrl });

      if (!source && !hasLocalFile) {
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: NOTIFICATION_MESSAGES.DOCUMENT_NO_LINK_DESC,
          placement: "topRight",
        });
        return;
      }

      setIsDownloading(true);

      try {
        if (hasLocalFile && localFile) {
          downloadBlob(localFile, resolvedFileName);
          notification.success({
            message: NOTIFICATION_MESSAGES.DOCUMENT_LOADED,
            placement: "topRight",
          });
          return;
        }

        if (!source) {
          throw new Error("unsupported source");
        }

        if (source.kind === "local") {
          const response = await fetch(source.blobUrl);
          const blob = await response.blob();
          downloadBlob(blob, resolvedFileName);
        } else {
          await downloadRemoteSource(source, resolvedFileName);
        }

        notification.success({
          message: NOTIFICATION_MESSAGES.DOCUMENT_LOADED,
          placement: "topRight",
        });
      } catch {
        notification.error({
          message: NOTIFICATION_MESSAGES.ERROR,
          description: NOTIFICATION_MESSAGES.DOCUMENT_LOAD_FAILED_DESC,
          placement: "topRight",
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [],
  );

  return { downloadDocument, isDownloading };
};
